import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Box, Flex, Grid, Text, VStack } from '@chakra-ui/react'
import { AnimatePresence, motion } from 'framer-motion'
import { DatePicker } from '@minetrace/ui/forms'
import { AdvancedMarker, InfoWindow, useMap } from '@vis.gl/react-google-maps'
import {
  LuRefreshCw, LuMap, LuEye, LuEyeOff, LuFlame, LuMapPin, LuShieldOff, LuScale,
  LuSparkles, LuLandmark, LuLayers, LuMapPinOff,
} from 'react-icons/lu'

import { analyticsService } from '../../../api/services/analytics.service'
import { restrictedZonesIntelService } from '../../../api/services/restrictedZonesIntel.service'
import type {
  OreOriginHeatmapResponse,
  OreOriginPoint,
  OreRiskLevel,
} from '../../../api/models/response/oreOriginHeatmap'
import type { RestrictedZoneFeature } from '../../../api/models/response/restrictedZoneIntel'
import { KMap, FitBounds, MarkerTooltip, HeatLayer } from '../../../lib/googleMaps'
import { ComponentPin, ElementPin } from '../../../lib/mapMarkers'
import { resolveUbigeoLabel } from '../../../lib/ubigeo'
import { useHasPermission } from '../../../auth/PermissionContext'
import { fonts, mineral } from '../../../theme'
import {
  ReportPage, ReportHeader, ActionButton, FilterBar, FilterField, VerdictBanner,
  KpiGrid, KpiTile, Panel, ToneBadge, Notice, EmptyState, ReportSkeleton,
} from '../../../components/analytics/ReportKit'
import { reportAccent, TONES, table, type Tone } from '../../../components/analytics/reportTheme'

/**
 * MAPA DE CALOR DE ORIGEN DE MINERAL (req. 1.7)
 *
 * Cruza el peso neto de balanza (TMH) con la georreferencia de los componentes
 * mineros. Datos: `GET /api/analytics/ore-origin-heatmap` — el tonelaje YA viene en
 * toneladas (antes el campo se llamaba "Kg" y se dividía entre 1000: salía 0,3 t).
 * El rango por defecto (90 días) coincide con el del backend.
 */

type LL = google.maps.LatLngLiteral

/** Periodo por defecto; el backend usa el mismo cuando no recibe fechas. */
const DEFAULT_RANGE_DAYS = 90
const PRESETS = [
  { days: 30, label: '30 días' },
  { days: 90, label: '90 días' },
  { days: 180, label: '6 meses' },
  { days: 365, label: '12 meses' },
]

const RISK_ORDER: OreRiskLevel[] = ['Green', 'Yellow', 'Red']
const RISK_TONE: Record<OreRiskLevel, Tone> = { Green: 'ok', Yellow: 'warn', Red: 'bad' }
const RISK_LABEL: Record<OreRiskLevel, string> = { Green: 'Apto', Yellow: 'Advertencia', Red: 'Bloqueo' }
const RISK_HINT: Record<OreRiskLevel, string> = {
  Green: 'REINFO vigente o formalizado, sin solape y con componentes ubicados',
  Yellow: 'En trámite, sin dato de REINFO o sin componentes georreferenciados',
  Red: 'REINFO suspendido / extinto o solape con zona de exclusión',
}
/** La sospecha de blanqueo se marca con el acento amatista (alerta propia de este reporte). */
const LAUNDERING = TONES.accent
/** Zonas de exclusión legal: lapislázuli, para no confundirse con el calor ni con el semáforo. */
const EXCLUSION = TONES.info

// Gradiente de calor (de "sin datos" a intenso). El primer color debe ser
// transparente para que la capa interpole desde el fondo del mapa.
const HEAT_GRADIENT = ['rgba(249,236,207,0)', mineral.oroSoft, mineral.oro, mineral.cobre, mineral.aji, '#9c3322']

// ── Fechas: día LOCAL (no UTC; después de las 19:00 en Lima toISOString ya es mañana) ──
function localIso(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}
function isoDaysAgo(days: number): string {
  const d = new Date()
  d.setDate(d.getDate() - (days - 1))
  return localIso(d)
}
function formatDay(iso: string | null | undefined): string {
  if (!iso) return '—'
  const [y, m, d] = iso.slice(0, 10).split('-')
  return y && m && d ? `${d}/${m}/${y}` : '—'
}

// ── Números es-PE ──
const nf1 = new Intl.NumberFormat('es-PE', { maximumFractionDigits: 1 })
const nf2 = new Intl.NumberFormat('es-PE', { maximumFractionDigits: 2 })
function fmtTons(t: number): string {
  return (Math.abs(t) < 10 ? nf2 : nf1).format(t)
}
function fmtPct(p: number): string {
  return `${nf1.format(p)} %`
}
function plural(n: number | null | undefined, one: string, many: string): string {
  const v = n ?? 0
  return `${v.toLocaleString('es-PE')} ${v === 1 ? one : many}`
}

/** Nombre del distrito a partir del ubigeo INEI (catálogo local `data/ubigeo.json`). */
function districtInfo(code: string | null | undefined): { name: string; detail: string } {
  if (!code || code === 'SIN_UBIGEO') return { name: 'Sin ubigeo', detail: 'Derecho minero sin distrito registrado' }
  const label = resolveUbigeoLabel(code)
  if (label === code) return { name: `Ubigeo ${code}`, detail: 'Código no encontrado en el catálogo INEI' }
  const parts = label.split(' / ')
  const name = parts[parts.length - 1] ?? label
  const detail = parts.slice(0, -1).reverse().join(', ')
  return { name, detail: detail || name }
}

function isValidLatLng(lat: number | null | undefined, lng: number | null | undefined): boolean {
  return lat != null && lng != null && Number.isFinite(lat) && Number.isFinite(lng)
    && Math.abs(lat) <= 90 && Math.abs(lng) <= 180 && !(lat === 0 && lng === 0)
}

// ── Capas del mapa ────────────────────────────────────────────────────────────

// Capa GeoJSON de zonas de exclusión sobre google.maps.Data. No interactiva.
function ExclusionLayer({ features, visible }: { features: RestrictedZoneFeature[]; visible: boolean }) {
  const map = useMap()
  const dataRef = useRef<google.maps.Data | null>(null)

  useEffect(() => {
    if (!map) return
    const data = new google.maps.Data()
    data.setStyle({
      strokeColor: EXCLUSION.color,
      strokeWeight: 1.5,
      fillColor: EXCLUSION.color,
      fillOpacity: 0.16,
      clickable: false,
      // Las zonas que llegan como PUNTO (p. ej. REINFO) se dibujaban con el pin rojo
      // por defecto de google.maps.Data y se confundían con orígenes en "Bloqueo".
      icon: {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 4,
        fillColor: EXCLUSION.color,
        fillOpacity: 0.85,
        strokeColor: '#ffffff',
        strokeWeight: 1,
      },
    })
    dataRef.current = data
    return () => {
      data.setMap(null)
      dataRef.current = null
    }
  }, [map])

  useEffect(() => {
    const data = dataRef.current
    if (!data || !map) return
    data.setMap(visible ? map : null)
  }, [map, visible])

  useEffect(() => {
    const data = dataRef.current
    if (!data) return
    data.forEach((f) => data.remove(f))
    for (const feat of features) {
      data.addGeoJson({ type: 'Feature', id: feat.id, geometry: feat.geometry, properties: {} })
    }
  }, [features])

  return null
}

interface Bbox {
  minLng: number
  minLat: number
  maxLng: number
  maxLat: number
}

// Informa el encuadre visible (con debounce) para pedir solo las zonas de exclusión que se ven.
function BboxReporter({ onChange }: { onChange: (b: Bbox) => void }) {
  const map = useMap()
  const timer = useRef<number | null>(null)
  const onChangeRef = useRef(onChange)
  useEffect(() => { onChangeRef.current = onChange }, [onChange])

  useEffect(() => {
    if (!map) return
    const fire = () => {
      const b = map.getBounds()
      if (!b) return
      const ne = b.getNorthEast()
      const sw = b.getSouthWest()
      onChangeRef.current({ minLng: sw.lng(), minLat: sw.lat(), maxLng: ne.lng(), maxLat: ne.lat() })
    }
    const idle = google.maps.event.addListenerOnce(map, 'idle', fire)
    const moveL = map.addListener('idle', () => {
      if (timer.current) window.clearTimeout(timer.current)
      timer.current = window.setTimeout(fire, 350)
    })
    return () => {
      idle.remove()
      moveL.remove()
      if (timer.current) window.clearTimeout(timer.current)
    }
  }, [map])
  return null
}

// Centra el mapa en un origen elegido desde la tabla.
function FocusOn({ target }: { target: { position: LL; nonce: number } | null }) {
  const map = useMap()
  useEffect(() => {
    if (!map || !target) return
    map.panTo(target.position)
    if ((map.getZoom() ?? 0) < 12) map.setZoom(12)
  }, [map, target])
  return null
}

interface MarkerEntry {
  id: string
  position: LL
  name: string
  type: string
  code: string | null
  origin: OreOriginPoint
}

// ── Página ────────────────────────────────────────────────────────────────────

export default function MapaOrigenMineralPage() {
  const canReveal = useHasPermission('analytics.oreHeatmap.revealSuppliers')

  const [data, setData] = useState<OreOriginHeatmapResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [dateFrom, setDateFrom] = useState<string>(() => isoDaysAgo(DEFAULT_RANGE_DAYS))
  const [dateTo, setDateTo] = useState<string>(() => localIso(new Date()))
  const [revealSuppliers, setRevealSuppliers] = useState(false)

  // Visibilidad de capas.
  const [showHeat, setShowHeat] = useState(true)
  const [showMarkers, setShowMarkers] = useState(true)
  const [showExclusion, setShowExclusion] = useState(true)

  // InfoWindow abierto y foco pedido desde la tabla de orígenes.
  const [openMarkerId, setOpenMarkerId] = useState<string | null>(null)
  const [focus, setFocus] = useState<{ position: LL; nonce: number } | null>(null)
  const mapBoxRef = useRef<HTMLDivElement>(null)

  // Capa de exclusión — guiada por el encuadre, reutiliza el endpoint de zonas restringidas.
  const [bbox, setBbox] = useState<Bbox | null>(null)
  const [exclusionFeatures, setExclusionFeatures] = useState<RestrictedZoneFeature[]>([])

  // Descarta respuestas viejas si el usuario cambia el filtro antes de que lleguen.
  const requestId = useRef(0)

  const fetchData = useCallback(async () => {
    const id = ++requestId.current
    setLoading(true)
    setError(null)
    try {
      const res = await analyticsService.oreOriginHeatmap({
        // Fecha vacía (se limpió el campo) → el backend aplica su default de 90 días.
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        revealSuppliers: revealSuppliers && canReveal,
      })
      if (id === requestId.current) setData(res)
    } catch (e) {
      if (id === requestId.current) setError(e instanceof Error ? e.message : String(e))
    } finally {
      if (id === requestId.current) setLoading(false)
    }
  }, [dateFrom, dateTo, revealSuppliers, canReveal])

  useEffect(() => { void fetchData() }, [fetchData])

  const fetchExclusion = useCallback(async () => {
    if (!bbox || !showExclusion) return
    try {
      const fc = await restrictedZonesIntelService.byBbox({ ...bbox, limit: 1500 })
      setExclusionFeatures(fc.features)
    } catch {
      // La capa de exclusión es complementaria: no bloquea el mapa de calor.
      setExclusionFeatures([])
    }
  }, [bbox, showExclusion])

  useEffect(() => { void fetchExclusion() }, [fetchExclusion])

  const origins = useMemo(() => data?.origins ?? [], [data])
  const kpis = data?.kpis
  const hasData = origins.length > 0

  const heatPoints = useMemo(
    () => origins
      .filter((o) => isValidLatLng(o.lat, o.lng) && o.totalNetWeightTons > 0)
      .map((o) => ({ lat: o.lat as number, lng: o.lng as number, weight: o.totalNetWeightTons })),
    [origins],
  )
  const maxWeight = useMemo(() => origins.reduce((m, o) => Math.max(m, o.totalNetWeightTons), 0), [origins])

  // Marcadores in situ por componente; si el D.M. no tiene componentes, su centroide.
  const markers = useMemo<MarkerEntry[]>(() => {
    const out: MarkerEntry[] = []
    for (const o of origins) {
      const comps = o.components.filter((c) => isValidLatLng(c.lat, c.lng))
      if (comps.length > 0) {
        for (const c of comps) {
          out.push({ id: c.id, position: { lat: c.lat, lng: c.lng }, name: c.name || c.componentTypeName, type: c.componentTypeName, code: c.componentTypeCode, origin: o })
        }
      } else if (isValidLatLng(o.lat, o.lng)) {
        out.push({ id: o.miningRightId, position: { lat: o.lat as number, lng: o.lng as number }, name: o.miningRightName, type: 'Derecho minero', code: null, origin: o })
      }
    }
    return out
  }, [origins])

  const fitPoints = useMemo<LL[]>(
    () => origins.filter((o) => isValidLatLng(o.lat, o.lng)).map((o) => ({ lat: o.lat as number, lng: o.lng as number })),
    [origins],
  )
  const unlocatedCount = origins.length - fitPoints.length

  // Reparto del tonelaje por nivel de semáforo (para las barras del panel).
  const tonsByRisk = useMemo(() => {
    const acc: Record<OreRiskLevel, number> = { Green: 0, Yellow: 0, Red: 0 }
    for (const o of origins) acc[o.riskLevel] += o.totalNetWeightTons
    return acc
  }, [origins])
  const suspicious = useMemo(() => origins.filter((o) => o.excessProduction), [origins])
  const total = kpis?.totalNetWeightTons ?? 0

  const topDistrict = kpis?.topConcentrationDistrict ? districtInfo(kpis.topConcentrationDistrict) : null

  // ── Veredicto ──
  const verdict = useMemo((): { tone: Tone; title: string; subtitle: string } | null => {
    if (!kpis) return null
    if (!hasData) {
      return { tone: 'neutral', title: 'Sin tonelaje pesado en el periodo', subtitle: 'No hay lotes con peso neto de balanza entre las fechas elegidas. Amplía el rango.' }
    }
    if (kpis.suspiciousCount > 0) {
      return {
        tone: 'bad',
        title: `Sospecha de blanqueo en ${plural(kpis.suspiciousCount, 'origen', 'orígenes')}`,
        subtitle: 'El tonelaje recibido supera la capacidad técnica declarada en el IGAFOM para el periodo. Revisa los marcadores amatista.',
      }
    }
    if (kpis.redCount > 0) {
      return {
        tone: 'bad',
        title: `${plural(kpis.redCount, 'origen', 'orígenes')} en bloqueo`,
        subtitle: `${fmtTons(tonsByRisk.Red)} t (${fmtPct(total > 0 ? (tonsByRisk.Red / total) * 100 : 0)}) provienen de orígenes con REINFO suspendido o en zona de exclusión.`,
      }
    }
    if (kpis.cleanMineralPercent >= 80) {
      return { tone: 'ok', title: 'Suministro mayormente limpio', subtitle: 'La mayor parte del tonelaje viene de orígenes aptos y sin solape con zonas de exclusión.' }
    }
    return {
      tone: 'warn',
      title: 'Suministro con advertencias',
      subtitle: 'Parte del tonelaje viene de orígenes en trámite, sin dato de REINFO o sin componentes georreferenciados.',
    }
  }, [kpis, hasData, tonsByRisk, total])

  const cleanTone: Tone | undefined = !kpis || !hasData
    ? undefined
    : kpis.cleanMineralPercent >= 80 ? 'ok' : kpis.cleanMineralPercent >= 50 ? 'warn' : 'bad'

  const applyPreset = (days: number) => {
    setDateFrom(isoDaysAgo(days))
    setDateTo(localIso(new Date()))
  }
  const activePreset = dateTo === localIso(new Date())
    ? PRESETS.find((p) => isoDaysAgo(p.days) === dateFrom)?.days ?? null
    : null

  const focusOrigin = (o: OreOriginPoint) => {
    const firstComp = o.components.find((c) => isValidLatLng(c.lat, c.lng))
    const position = firstComp
      ? { lat: firstComp.lat, lng: firstComp.lng }
      : isValidLatLng(o.lat, o.lng) ? { lat: o.lat as number, lng: o.lng as number } : null
    if (!position) return
    setShowMarkers(true)
    setOpenMarkerId(firstComp ? firstComp.id : o.miningRightId)
    setFocus({ position, nonce: Date.now() })
    mapBoxRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  const refresh = () => { void fetchData(); void fetchExclusion() }

  return (
    <ReportPage>
      <ReportHeader
        icon={LuMap}
        title="Mapa de Origen Mineral"
        subtitle="Peso neto de balanza cruzado con la georreferencia de los componentes mineros: de dónde viene el mineral y con qué riesgo."
        actions={(
          <>
            {canReveal && (
              <ActionButton
                icon={revealSuppliers ? LuEyeOff : LuEye}
                label={revealSuppliers ? 'Ocultar proveedores' : 'Ver proveedores'}
                onClick={() => setRevealSuppliers((v) => !v)}
                title="Mostrar u ocultar razón social y RUC (Ley 29733)"
              />
            )}
            <ActionButton icon={LuRefreshCw} label="Actualizar" variant="primary" loading={loading} onClick={refresh} />
          </>
        )}
      />

      <FilterBar
        note={data ? (
          <>
            Periodo aplicado: <b>{formatDay(data.dateFrom)}</b> – <b>{formatDay(data.dateTo)}</b>
            {data.rangeDays ? ` (${plural(data.rangeDays, 'día', 'días')})` : ''} · Toneladas húmedas (TMH) del pesaje neto en planta; se excluyen lotes anulados o rechazados.
          </>
        ) : undefined}
      >
        <FilterField label="Desde" width="180px">
          <DatePicker value={dateFrom} onChange={setDateFrom} maxDate={dateTo || undefined} />
        </FilterField>
        <FilterField label="Hasta" width="180px">
          <DatePicker value={dateTo} onChange={setDateTo} minDate={dateFrom || undefined} />
        </FilterField>
        <FilterField label="Rango rápido" width="auto">
          <Flex gap={1.5} wrap="wrap">
            {PRESETS.map((p) => (
              <Chip key={p.days} active={activePreset === p.days} onClick={() => applyPreset(p.days)}>{p.label}</Chip>
            ))}
          </Flex>
        </FilterField>
      </FilterBar>

      {error && <Notice tone="bad">No se pudo cargar el mapa de origen: {error}</Notice>}

      {!data && loading ? (
        <ReportSkeleton />
      ) : (
        <>
          {verdict && (
            <VerdictBanner
              key={`${verdict.tone}-${verdict.title}`}
              tone={verdict.tone}
              title={verdict.title}
              subtitle={verdict.subtitle}
              meta={hasData && kpis ? (
                <>
                  <Text fontSize="11px" textTransform="uppercase" letterSpacing="0.06em" color={mineral.ink3}>Mineral limpio</Text>
                  <Text fontFamily={fonts.display} fontWeight={800} fontSize="22px" color={TONES[verdict.tone].deep}>{fmtPct(kpis.cleanMineralPercent)}</Text>
                </>
              ) : undefined}
            />
          )}

          <KpiGrid>
            <KpiTile
              index={0}
              label="Índice de mineral limpio"
              icon={LuSparkles}
              tone={cleanTone}
              value={kpis && hasData ? kpis.cleanMineralPercent : null}
              format={(n) => nf1.format(n)}
              unit="%"
              hint="Tonelaje de orígenes aptos sobre el total"
            />
            <KpiTile
              index={1}
              label="Tonelaje neto recibido"
              icon={LuScale}
              value={kpis && hasData ? kpis.totalNetWeightTons : null}
              format={fmtTons}
              unit="t"
              hint={kpis && hasData ? `${plural(kpis.lotCount, 'lote pesado', 'lotes pesados')} en balanza` : 'Sin lotes pesados'}
            />
            <KpiTile
              index={2}
              label="Orígenes (derechos mineros)"
              icon={LuMapPin}
              value={kpis && hasData ? kpis.originCount : null}
              format={(n) => Math.round(n).toLocaleString('es-PE')}
              hint={kpis && hasData ? `${kpis.greenCount} aptos · ${kpis.yellowCount} advertencia · ${kpis.redCount} bloqueo` : undefined}
            />
            <KpiTile
              index={3}
              label="Mayor dependencia"
              icon={LuLandmark}
              tone={kpis && hasData && kpis.topConcentrationPercent >= 50 ? 'warn' : undefined}
              value={topDistrict && hasData ? topDistrict.name : null}
              hint={topDistrict && kpis && hasData ? `${fmtPct(kpis.topConcentrationPercent)} del tonelaje · ${topDistrict.detail}` : undefined}
            />
          </KpiGrid>

          <Grid templateColumns={{ base: 'minmax(0, 1fr)', xl: '340px minmax(0, 1fr)' }} gap={4} alignItems="start">
            {/* Panel lateral: semáforo, concentración y privacidad */}
            <VStack align="stretch" gap={4} order={{ base: 2, xl: 1 }} minW={0}>
              <Panel index={0} title="Semáforo de origen" subtitle="Reparto del tonelaje por nivel de riesgo">
                <VStack align="stretch" gap={3.5}>
                  {RISK_ORDER.map((lvl, i) => {
                    const t = TONES[RISK_TONE[lvl]]
                    const count = lvl === 'Green' ? kpis?.greenCount : lvl === 'Yellow' ? kpis?.yellowCount : kpis?.redCount
                    const pct = total > 0 ? (tonsByRisk[lvl] / total) * 100 : 0
                    return (
                      <RiskBar
                        key={lvl}
                        index={i}
                        color={t.color}
                        label={RISK_LABEL[lvl]}
                        hint={RISK_HINT[lvl]}
                        count={hasData ? count ?? 0 : null}
                        tons={hasData ? tonsByRisk[lvl] : null}
                        pct={pct}
                      />
                    )
                  })}
                  <Box borderTop={`1px dashed ${mineral.line}`} pt={3}>
                    <Flex align="center" justify="space-between" gap={2}>
                      <Flex align="center" gap={2} minW={0}>
                        <Box w="10px" h="10px" borderRadius="full" bg={LAUNDERING.color} flexShrink={0} />
                        <Text fontSize="13px" fontWeight={600} color={mineral.ink}>Sospecha de blanqueo</Text>
                      </Flex>
                      <Text fontFamily={fonts.display} fontWeight={800} fontSize="16px" color={suspicious.length > 0 ? LAUNDERING.deep : mineral.ink3}>
                        {hasData ? suspicious.length : '—'}
                      </Text>
                    </Flex>
                    <Text fontSize="11.5px" color={mineral.ink3} mt={1}>
                      Recibido por encima de la capacidad IGAFOM × días del periodo.
                    </Text>
                  </Box>
                </VStack>
              </Panel>

              <Panel index={1} title="Concentración por distrito" subtitle="Dónde se concentra el suministro">
                {data && data.concentrationByDistrict.length > 0 ? (
                  <VStack align="stretch" gap={3}>
                    {data.concentrationByDistrict.slice(0, 6).map((c, i) => {
                      const d = districtInfo(c.ubigeoCode)
                      return (
                        <Box key={c.ubigeoCode} title={c.ubigeoCode !== 'SIN_UBIGEO' ? `Ubigeo ${c.ubigeoCode}` : undefined}>
                          <Flex justify="space-between" align="baseline" gap={2} mb={1}>
                            <Box minW={0}>
                              <Text fontSize="13px" fontWeight={600} color={mineral.ink} lineClamp={1}>{d.name}</Text>
                              <Text fontSize="11px" color={mineral.ink3} lineClamp={1}>{d.detail}</Text>
                            </Box>
                            <Box textAlign="right" flexShrink={0}>
                              <Text fontSize="13px" fontWeight={700} color={mineral.ink}>{fmtPct(c.percent)}</Text>
                              <Text fontSize="11px" color={mineral.ink3}>{fmtTons(c.netWeightTons)} t</Text>
                            </Box>
                          </Flex>
                          <Bar pct={c.percent} color={reportAccent.color} index={i} />
                        </Box>
                      )
                    })}
                    {data.concentrationByDistrict.length > 6 && (
                      <Text fontSize="11.5px" color={mineral.ink3}>
                        y {plural(data.concentrationByDistrict.length - 6, 'distrito más', 'distritos más')}
                      </Text>
                    )}
                  </VStack>
                ) : (
                  <Text fontSize="13px" color={mineral.ink3}>—</Text>
                )}
              </Panel>

              <Notice tone="neutral">
                {data?.suppliersRevealed
                  ? 'Se muestran razón social y RUC de los proveedores.'
                  : 'Proveedores seudonimizados (COD-###) por la Ley 29733 de protección de datos.'}
              </Notice>
            </VStack>

            {/* Mapa: protagonista */}
            <Box order={{ base: 1, xl: 2 }} minW={0} ref={mapBoxRef}>
              <Panel
                index={2}
                padded={false}
                title="Mapa de origen"
                subtitle={unlocatedCount > 0
                  ? `${plural(unlocatedCount, 'origen sin georreferencia válida', 'orígenes sin georreferencia válida')}: cuentan en los totales pero no se dibujan.`
                  : 'Calor = tonelaje acumulado; pin = componente minero coloreado por semáforo.'}
                actions={(
                  <Flex gap={1.5} wrap="wrap" justify="flex-end">
                    <Chip icon={LuFlame} active={showHeat} onClick={() => setShowHeat((v) => !v)}>Calor</Chip>
                    <Chip icon={LuMapPin} active={showMarkers} onClick={() => setShowMarkers((v) => !v)}>Pines</Chip>
                    <Chip icon={LuShieldOff} active={showExclusion} onClick={() => setShowExclusion((v) => !v)}>Exclusión</Chip>
                  </Flex>
                )}
              >
                <Box position="relative" h={{ base: '440px', md: '560px', xl: '640px' }}>
                  <KMap defaultCenter={{ lat: -12, lng: -76 }} defaultZoom={6} mapTypeId="hybrid" mapTypeControl>
                    <BboxReporter onChange={setBbox} />
                    <ExclusionLayer features={exclusionFeatures} visible={showExclusion} />
                    <HeatLayer points={heatPoints} max={maxWeight} gradient={HEAT_GRADIENT} radiusMeters={4000} opacity={0.75} visible={showHeat} />

                    {showMarkers && markers.map((m) => {
                      const o = m.origin
                      // Sospecha de blanqueo → pin amatista más grande (req. 1.7.3).
                      const color = o.excessProduction ? LAUNDERING.color : TONES[RISK_TONE[o.riskLevel]].color
                      const size = o.excessProduction ? 42 : 34
                      return (
                        <AdvancedMarker key={m.id} position={m.position} onClick={() => setOpenMarkerId(m.id)}>
                          <MarkerTooltip title={m.type} subtitle={m.name} hoverOnly>
                            {m.code === null && o.components.length === 0
                              ? <ElementPin kind="miningRight" color={color} size={size} badge={o.excessProduction ? '!' : undefined} />
                              : <ComponentPin code={m.code} color={color} size={size} badge={o.excessProduction ? '!' : undefined} />}
                          </MarkerTooltip>
                        </AdvancedMarker>
                      )
                    })}

                    {showMarkers && markers.filter((m) => m.id === openMarkerId).map((m) => (
                      <InfoWindow
                        key={`info-${m.id}`}
                        position={m.position}
                        pixelOffset={[0, m.origin.excessProduction ? -46 : -38]}
                        onCloseClick={() => setOpenMarkerId(null)}
                      >
                        <OriginInfo marker={m} />
                      </InfoWindow>
                    ))}

                    <FitBounds points={fitPoints} padding={48} maxZoom={11} />
                    <FocusOn target={focus} />
                  </KMap>

                  <AnimatePresence>
                    {loading && (
                      <motion.div
                        key="loading"
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        style={{ position: 'absolute', top: 12, left: '50%', translateX: '-50%', zIndex: 5 }}
                      >
                        <Flex align="center" gap={2} bg="white" px={3} py={1.5} borderRadius="full" boxShadow="0 4px 14px rgba(0,0,0,0.18)">
                          <motion.span animate={{ rotate: 360 }} transition={{ duration: 0.9, repeat: Infinity, ease: 'linear' }} style={{ display: 'flex', color: reportAccent.color }}>
                            <LuRefreshCw size={13} />
                          </motion.span>
                          <Text fontSize="12px" fontWeight={600} color={mineral.ink2}>Cargando…</Text>
                        </Flex>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <MapLegend />

                  {!loading && data && !hasData && (
                    <Flex position="absolute" inset={0} align="center" justify="center" pointerEvents="none" zIndex={4}>
                      <Box bg="white" borderRadius="16px" boxShadow="0 10px 30px rgba(0,0,0,0.2)" pointerEvents="auto" maxW="360px" mx={4}>
                        <EmptyState icon={LuMapPinOff} title="Sin tonelaje en el periodo" hint="No hay lotes con peso neto de balanza entre las fechas elegidas. Prueba con un rango más amplio." />
                      </Box>
                    </Flex>
                  )}
                </Box>
              </Panel>
            </Box>
          </Grid>

          {hasData && (
            <Panel index={3} padded={false} title="Orígenes del periodo" subtitle="Ordenados por tonelaje. Toca una fila para ubicarla en el mapa.">
              <Box overflowX="auto">
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
                  <thead>
                    <tr>
                      <th style={table.th}>Derecho minero</th>
                      <th style={table.th}>Proveedor</th>
                      <th style={table.th}>Distrito</th>
                      <th style={{ ...table.th, textAlign: 'right' }}>Lotes</th>
                      <th style={{ ...table.th, textAlign: 'right' }}>Tonelaje</th>
                      <th style={{ ...table.th, textAlign: 'right' }}>Uso de capacidad</th>
                      <th style={table.th}>Semáforo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {origins.map((o) => (
                      <OriginRow key={o.miningRightId} origin={o} share={total > 0 ? (o.totalNetWeightTons / total) * 100 : 0} onFocus={() => focusOrigin(o)} />
                    ))}
                  </tbody>
                </table>
              </Box>
            </Panel>
          )}
        </>
      )}
    </ReportPage>
  )
}

// ── Piezas locales ────────────────────────────────────────────────────────────

// Píldora conmutable (rango rápido y capas). Acento amatista cuando está activa.
function Chip({ active, onClick, icon: Icon, children }: { active: boolean; onClick: () => void; icon?: typeof LuLayers; children: ReactNode }) {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileTap={{ scale: 0.95 }}
      aria-pressed={active}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, height: 32, padding: '0 11px', borderRadius: 9,
        fontSize: 12.5, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
        border: `1px solid ${active ? reportAccent.color : mineral.line}`,
        background: active ? reportAccent.soft : '#fff',
        color: active ? reportAccent.deep : mineral.ink3,
        transition: 'background .15s, border-color .15s, color .15s',
      }}
    >
      {Icon && <Icon size={13} />}
      {children}
    </motion.button>
  )
}

// Barra de proporción que crece al llegar el dato.
function Bar({ pct, color, index = 0 }: { pct: number; color: string; index?: number }) {
  return (
    <Box bg={mineral.sunk} borderRadius="full" h="7px" overflow="hidden">
      <motion.div
        initial={{ width: 0 }}
        animate={{ width: `${Math.min(Math.max(pct, 0), 100)}%` }}
        transition={{ delay: 0.05 * index, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        style={{ height: '100%', background: color, borderRadius: 999 }}
      />
    </Box>
  )
}

function RiskBar({ color, label, hint, count, tons, pct, index }: {
  color: string; label: string; hint: string; count: number | null; tons: number | null; pct: number; index: number
}) {
  return (
    <Box title={hint}>
      <Flex justify="space-between" align="baseline" gap={2} mb={1}>
        <Flex align="center" gap={2} minW={0}>
          <Box w="10px" h="10px" borderRadius="full" bg={color} flexShrink={0} />
          <Text fontSize="13px" fontWeight={600} color={mineral.ink}>{label}</Text>
          <Text fontSize="12px" color={mineral.ink3}>{count === null ? '—' : plural(count, 'origen', 'orígenes')}</Text>
        </Flex>
        <Text fontSize="12px" color={mineral.ink2} flexShrink={0}>
          {tons === null ? '—' : `${fmtTons(tons)} t · ${fmtPct(pct)}`}
        </Text>
      </Flex>
      <Bar pct={tons === null ? 0 : pct} color={color} index={index} />
    </Box>
  )
}

function MapLegend() {
  const items: Array<{ color: string; label: string; shape: 'dot' | 'square' | 'ring' }> = [
    ...RISK_ORDER.map((l) => ({ color: TONES[RISK_TONE[l]].color, label: RISK_LABEL[l], shape: 'dot' as const })),
    { color: LAUNDERING.color, label: 'Sospecha de blanqueo', shape: 'ring' },
    { color: EXCLUSION.color, label: 'Zona de exclusión', shape: 'square' },
  ]
  return (
    <Box position="absolute" left={3} bottom={6} zIndex={3} bg="rgba(255,255,255,0.95)" borderRadius="12px" px={3} py={2.5}
      boxShadow="0 4px 14px rgba(0,0,0,0.18)" display={{ base: 'none', sm: 'block' }}>
      <VStack align="flex-start" gap={1.5}>
        {items.map((it) => (
          <Flex key={it.label} align="center" gap={2}>
            <Box w="11px" h="11px" flexShrink={0}
              borderRadius={it.shape === 'square' ? '3px' : 'full'}
              bg={it.shape === 'ring' ? 'transparent' : it.shape === 'square' ? `${it.color}55` : it.color}
              border={it.shape === 'dot' ? 'none' : `2px solid ${it.color}`} />
            <Text fontSize="11.5px" fontWeight={600} color={mineral.ink2}>{it.label}</Text>
          </Flex>
        ))}
        <Flex align="center" gap={2} pt={0.5}>
          <Box w="40px" h="7px" borderRadius="full" bg={`linear-gradient(90deg, ${HEAT_GRADIENT.slice(1).join(', ')})`} />
          <Text fontSize="11px" color={mineral.ink3}>Más tonelaje</Text>
        </Flex>
      </VStack>
    </Box>
  )
}

function OriginInfo({ marker: m }: { marker: MarkerEntry }) {
  const o = m.origin
  const d = districtInfo(o.ubigeoCode)
  return (
    <Box minW="250px" maxW="300px" p={1} fontFamily={fonts.body}>
      <Flex align="center" gap={2} mb={2} wrap="wrap">
        <ToneBadge tone={RISK_TONE[o.riskLevel]}>{RISK_LABEL[o.riskLevel]}</ToneBadge>
        {o.excessProduction && <ToneBadge tone="accent">Sospecha de blanqueo</ToneBadge>}
      </Flex>
      <InfoRow label="Componente / labor" value={m.name || m.type} hint={m.type !== m.name ? m.type : undefined} />
      <InfoRow label="Derecho minero" value={o.miningRightName || '—'} hint={o.miningRightCode || undefined} />
      <InfoRow label="Proveedor" value={o.supplierLabel || '—'} hint={o.supplierRuc ?? undefined} />
      <InfoRow label="Distrito" value={d.name} hint={d.detail} />
      <Grid templateColumns="1fr 1fr" columnGap={3}>
        <InfoRow label="REINFO" value={o.reinfoStatus ?? '—'} />
        <InfoRow label="Tonelaje" value={`${fmtTons(o.totalNetWeightTons)} t`} hint={plural(o.lotCount, 'lote', 'lotes')} />
      </Grid>
      <InfoRow label="Capacidad IGAFOM" value={o.productiveCapacity ?? '—'} />
      {o.excessProduction && (
        <Text fontSize="11.5px" color={LAUNDERING.deep} fontWeight={700} mt={1}>
          Recibido {o.excessRatio != null ? fmtPct(o.excessRatio * 100) : 'más del 100 %'} de la capacidad técnica del periodo.
        </Text>
      )}
      {o.inExclusionZone && (
        <Text fontSize="11.5px" color={TONES.bad.deep} fontWeight={700} mt={1}>Solapa una zona de exclusión legal.</Text>
      )}
    </Box>
  )
}

function InfoRow({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Box mb={1.5} minW={0}>
      <Text fontSize="10px" color={mineral.ink3} textTransform="uppercase" fontWeight={700} letterSpacing="0.05em">{label}</Text>
      <Text fontSize="13px" color={mineral.ink} lineHeight="1.3">{value}</Text>
      {hint && <Text fontSize="11px" color={mineral.ink3} fontFamily={fonts.mono}>{hint}</Text>}
    </Box>
  )
}

function OriginRow({ origin: o, share, onFocus }: { origin: OreOriginPoint; share: number; onFocus: () => void }) {
  const located = isValidLatLng(o.lat, o.lng) || o.components.some((c) => isValidLatLng(c.lat, c.lng))
  const ratio = o.excessRatio
  return (
    <Box
      as="tr"
      onClick={located ? onFocus : undefined}
      cursor={located ? 'pointer' : 'default'}
      _hover={{ bg: mineral.surfaceAlt }}
      title={located ? 'Ubicar en el mapa' : 'Sin georreferencia válida'}
    >
      <td style={table.td}>
        <Text fontWeight={600} color={mineral.ink} lineClamp={1}>{o.miningRightName || '—'}</Text>
        <Text fontSize="11px" color={mineral.ink3} fontFamily={fonts.mono}>{o.miningRightCode || '—'}</Text>
      </td>
      <td style={table.td}>
        <Text lineClamp={1}>{o.supplierLabel || '—'}</Text>
        {o.supplierRuc && <Text fontSize="11px" color={mineral.ink3} fontFamily={fonts.mono}>{o.supplierRuc}</Text>}
      </td>
      <td style={table.td}>
        <DistrictCell code={o.ubigeoCode} />
      </td>
      <td style={{ ...table.td, textAlign: 'right' }}>{nf1.format(o.lotCount)}</td>
      <td style={{ ...table.td, textAlign: 'right', whiteSpace: 'nowrap' }}>
        <Text fontWeight={700} color={mineral.ink}>{fmtTons(o.totalNetWeightTons)} t</Text>
        <Text fontSize="11px" color={mineral.ink3}>{fmtPct(share)}</Text>
      </td>
      <td style={{ ...table.td, textAlign: 'right', whiteSpace: 'nowrap' }}>
        {ratio == null
          ? <Text color={mineral.ink4}>—</Text>
          : <Text fontWeight={700} color={ratio > 1 ? LAUNDERING.deep : mineral.ink2}>{fmtPct(ratio * 100)}</Text>}
      </td>
      <td style={table.td}>
        <Flex gap={1.5} wrap="wrap">
          <ToneBadge tone={RISK_TONE[o.riskLevel]}>{RISK_LABEL[o.riskLevel]}</ToneBadge>
          {o.excessProduction && <ToneBadge tone="accent">Blanqueo</ToneBadge>}
        </Flex>
      </td>
    </Box>
  )
}

function DistrictCell({ code }: { code: string | null }) {
  if (!code) return <Text color={mineral.ink4}>—</Text>
  const d = districtInfo(code)
  return (
    <Box title={`Ubigeo ${code}`}>
      <Text lineClamp={1}>{d.name}</Text>
      <Text fontSize="11px" color={mineral.ink3} lineClamp={1}>{d.detail}</Text>
    </Box>
  )
}
